/**
 * @flow
 */

import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Form, Input, Button } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';

import TypeField from './TypeField';
import EntityField from './EntityField';
import PaymentTypeField from './PaymentTypeField';
import PaymentTypeFieldMembership from './PaymentTypeFieldMembership';
import QuantityField from '../../../containers/details/invoice/QuantityField';
import { MasterSelect, CabinetSelect } from '../../../containers/form';

// flow types
import type { Intl } from '../../../../../type';

import withModal from '../../../../../lib/withModal';
import MembershipDetails from '../../../../memberships/containers/details/MembershipDetails';

const intlMessages = {
    type: defineMessages({
        placeholder: {
            id:
                'pages.appointments.details.invoice.InvoiceFields.typePlaceholder',
            defaultMessage: 'Type',
        },
    }),
    item: defineMessages({
        placeholder: {
            id:
                'pages.appointments.details.invoice.InvoiceFields.itemPlaceholder',
            defaultMessage: 'Service or Product',
        },
    }),
    seller: defineMessages({
        placeholder: {
            id:
                'pages.appointments.details.invoice.InvoiceFields.sellerPlaceholder',
            defaultMessage: 'Seller',
        },
    }),
    quantity: defineMessages({
        placeholder: {
            id:
                'pages.appointments.details.invoice.InvoiceFields.quantityPlaceholder',
            defaultMessage: 'Qty.',
        },
    }),
    price: defineMessages({
        placeholder: {
            id:
                'pages.appointments.details.invoice.InvoiceFields.pricePlaceholder',
            defaultMessage: 'Price',
        },
    }),
    paymentType: defineMessages({
        placeholder: {
            id:
                'pages.appointments.details.invoice.InvoiceFields.paymentTypePlaceholder',
            defaultMessage: 'Select payment type',
        },
    }),
};

type InvoiceFieldsProps = {
    index: number,
    onRemove: Function,
    readOnly: boolean,
    intl: Intl,
    paid: boolean,
    masters: Array<Object>,
    cabinets: Array<Object>,
    sellerType: ?string,
    membership: ?Object,
    paidWithMembership: ?Object,
    allServices: Array<Object>,
    invoice: ?Array<Object>,
};

const RemoveButton = styled(Button)`
    margin-right: 0px !important;
`;

const InfoButtonComponent = styled(Button)`
    &&.disabled {
        box-shadow: 0 0 0 1px rgba(34, 36, 38, 0.15) inset !important;
    }
`;

const InfoButtonTrigger = withModal(({ open, onOpen, onClose, membership }) => (
    <MembershipDetails
        membership={membership}
        open={open}
        onClose={onClose}
        trigger={
            <InfoButtonComponent
                onClick={e => {
                    e.preventDefault();
                    onOpen();
                }}
                icon="info"
                basic
                disabled={!membership}
            />
        }
    />
));

function InvoiceFields({
    index,
    onRemove,
    readOnly,
    intl,
    paid,
    masters,
    cabinets,
    sellerType,
    membership,
    paidWithMembership,
    allServices,
    invoice,
}: InvoiceFieldsProps) {
    const { control, setValue } = useFormContext();
    const fieldPath = `items.${index}`;
    const [infoVisible, setInfoVisible] = useState(false);

    const change = (name, val) => setValue(name, val);

    return (
        <Form.Group>
            <Controller
                name={`${fieldPath}.type`}
                control={control}
                render={({ field: { ref: _ref, value, onChange } }) => (
                    <TypeField
                        value={value}
                        onChange={(e, { value: v }) => onChange(v)}
                        width={3}
                        placeholder={intl.formatMessage(
                            intlMessages.type.placeholder,
                        )}
                        fluid
                        disabled={readOnly}
                    />
                )}
            />
            <Controller
                name={`${fieldPath}.entity`}
                control={control}
                render={({ field: { ref: _ref, value, onChange } }) => (
                    <EntityField
                        value={value}
                        onChange={(e, data) => onChange(data?.value ?? e)}
                        name={`${fieldPath}.entity`}
                        fieldPath={fieldPath}
                        paid={paid}
                        change={change}
                        width={4}
                        placeholder={intl.formatMessage(
                            intlMessages.item.placeholder,
                        )}
                        fluid
                        search
                        disabled={readOnly}
                    />
                )}
            />
            {sellerType === 'CABINET' ? (
                <Controller
                    name={`${fieldPath}.seller`}
                    control={control}
                    render={({ field: { ref: _ref, value, onChange } }) => (
                        <CabinetSelect
                            value={value}
                            // CabinetSelect doesn't override onChange — Semantic UI calls (e, {value})
                            onChange={(e, { value: v }) => onChange(v)}
                            width={3}
                            placeholder={intl.formatMessage(
                                intlMessages.seller.placeholder,
                            )}
                            fluid
                            search
                            disabled={readOnly}
                            cabinets={cabinets}
                        />
                    )}
                />
            ) : (
                <Controller
                    name={`${fieldPath}.seller`}
                    control={control}
                    render={({ field: { ref: _ref, value, onChange } }) => (
                        <MasterSelect
                            value={value}
                            // MasterSelect.withHandlers calls onChange(value) with plain string
                            onChange={onChange}
                            width={3}
                            placeholder={intl.formatMessage(
                                intlMessages.seller.placeholder,
                            )}
                            fluid
                            search
                            disabled={readOnly}
                            masters={masters}
                        />
                    )}
                />
            )}
            <Controller
                name={`${fieldPath}.quantity`}
                control={control}
                render={({ field: { ref: _ref, value, onChange } }) => (
                    <QuantityField
                        value={value}
                        onChange={(e, { value: v }) => onChange(v)}
                        name={`${fieldPath}.quantity`}
                        width={2}
                        placeholder={intl.formatMessage(
                            intlMessages.quantity.placeholder,
                        )}
                        fluid
                        disabled={paid}
                    />
                )}
            />
            <Controller
                name={`${fieldPath}.price`}
                control={control}
                render={({ field: { ref: _ref, ...fieldProps } }) => (
                    <Input
                        {...fieldProps}
                        // Keep the input controlled from first render: a new
                        // item's price is undefined until set, which would
                        // otherwise flip it from uncontrolled to controlled.
                        value={fieldProps.value ?? ''}
                        width={2}
                        placeholder={intl.formatMessage(
                            intlMessages.price.placeholder,
                        )}
                        fluid
                        disabled={paid}
                    />
                )}
            />
            <Controller
                name={`${fieldPath}.paymentType`}
                control={control}
                render={({ field: { ref: _ref, value, onChange } }) => {
                    const PaymentComponent =
                        membership || paidWithMembership
                            ? PaymentTypeFieldMembership
                            : PaymentTypeField;
                    return (
                        <PaymentComponent
                            value={value}
                            onChange={(e, { value: v }) => onChange(v)}
                            width={2}
                            placeholder={intl.formatMessage(
                                intlMessages.paymentType.placeholder,
                            )}
                            fluid
                            disabled={paid}
                            membership={membership}
                            fieldPath={fieldPath}
                            change={change}
                            allServices={allServices}
                            setInfoVisible={setInfoVisible}
                            invoice={invoice}
                        />
                    );
                }}
            />

            {/* Always render the trailing action column so the six fields are
                compressed identically in every row and stay aligned. When the
                row is read-only with no info button, a hidden delete button
                reserves the exact same width (visibility:hidden keeps its box
                in the flexbox layout, unlike an empty field). */}
            <Form.Field>
                {!readOnly ? (
                    <RemoveButton
                        onClick={onRemove}
                        color="red"
                        icon="trash"
                        basic
                    />
                ) : membership && (infoVisible || paidWithMembership) ? (
                    <InfoButtonTrigger membership={membership} />
                ) : (
                    <RemoveButton
                        icon="trash"
                        basic
                        style={{ visibility: 'hidden' }}
                    />
                )}
            </Form.Field>
        </Form.Group>
    );
}

export default injectIntl(InvoiceFields);
